<template>
  <div class="flex flex-col h-full">
    <div class="bg-(--ui-bg) rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <h4 class="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <UIcon name="i-heroicons-pencil-square" class="text-primary-500 dark:text-primary-400"/>
        {{ t('notes.addNewNote') }}
      </h4>
      <form @submit.prevent="submitNote" class="space-y-3">
        <UTextarea
          v-model="noteText"
          color="neutral"
          highlight
          :placeholder="t('notes.placeholderText')"
          size="sm"
          class="w-full"
          :rows="3"
          :maxlength="1000"
          :ui="{
            base: 'text-sm resize-none h-18'
          }"
        >
          <template #trailing>
            <div id="character-count" class="text-xs text-muted tabular-nums">
              {{ noteText.length }}/{{ 1000 }}
            </div>
          </template>
        </UTextarea>
        <div class="flex justify-end gap-2">
          <UButton
            type="submit"
            color="primary"
            class="cursor-pointer text-white!"
            :disabled="!noteText.trim() || isSubmittingNote"
            :loading="isSubmittingNote"
          >
            {{ t('notes.submitNote') }}
          </UButton>
        </div>
      </form>
    </div>

    <div v-if="workflow && workflow.notes" class="flex-1 bg-(--ui-bg) rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div v-if="workflow.notes.length === 0" class="text-center py-8">
        <UIcon name="i-heroicons-chat-bubble-left-right" class="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p class="text-gray-500 dark:text-gray-400">{{ t('notes.noNotesYet') }}</p>
      </div>

      <div v-else class="space-y-4">
        <div v-for="note in sortedNotes" :key="note.id" class="flex gap-3 p-3 bg-(--ui-bg-muted) rounded-lg border border-gray-100 dark:border-gray-600">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
              {{ getUserInitials(note.createdBy) }}
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-sm font-medium text-gray-900 dark:text-white">
                {{ note.createdBy?.fullName }}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {{ note.createdDate ? formatDate(note.createdDate, 'datetime', locale) : '-' }}
              </span>
            </div>
            <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {{ note.text }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatDate } from "@/utils/date.ts";
import { useI18n } from "vue-i18n";
import { useApiAdapter, useUser } from "@/store";
import { api, type WorkflowDto } from "@/api/eOrdersApi.ts";
import { showError, showSuccess, showValidationErrors } from "@/utils/utils.ts";

const props = defineProps<{
  workflow: WorkflowDto | null;
  loading?: boolean
}>();

const emit = defineEmits(['reload']);

const { t, locale } = useI18n()
const userStore = useUser();
const apiAdapter = useApiAdapter();

const workflow = computed(() => props.workflow);

const sortedNotes = computed(() => {
  if (!workflow.value?.notes) return [];

  return [...workflow.value.notes].sort((a, b) => {
    const dateA = new Date(a.createdDate || 0);
    const dateB = new Date(b.createdDate || 0);
    return dateB.getTime() - dateA.getTime();
  });
});

const noteText = ref('')

const isSubmittingNote = ref(false)

const submitNote = async () => {
  isSubmittingNote.value = true

  try {
    const patch = [{
      op: 'add',
      path: '/Notes/-',
      value: {
        Text: noteText.value.trim()
      }
    }];

    if (patch.length === 0) {
      return;
    }

    const patchWorkflow = await apiAdapter.invoke<WorkflowDto>(
        requestConfig => api.patchWorkflow(workflow.value?.externalReference, patch, requestConfig),
        (error) => showValidationErrors(error, t)
    );

    if (patchWorkflow) {
      emit('reload');
      showSuccess(t('notes.noteAddedSuccessfully'))
    }
  } catch (error) {
    showError(t('notes.failedToAddNote'))
    console.error('Error adding note:', error)
  } finally {
    isSubmittingNote.value = false
  }
}

const getUserInitials = (user): string => {
  const names = user.fullName.split(' ');
  if (names.length > 1) {
    return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
  } else if (names.length === 1 && names[0].length > 0) {
    return names[0].charAt(0).toUpperCase();
  } else {
    return '';
  }
}
</script>